import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CogIcon, 
  DocumentTextIcon, 
  TableCellsIcon, 
  DocumentArrowDownIcon,
  ChartBarIcon 
} from '@heroicons/react/24/outline';
import { teachersApi, classesApi, timetableApi, learnersApi } from '../services/api';

interface ScheduleProgress {
  teachers_imported: boolean;
  classes_imported: boolean;
  learners_imported: boolean;
  total_learners: number;
  total_classes: number;
  allocated_slots: number;
  total_required_slots: number;
  completion_percentage: number;
}

const LandingPage: React.FC = () => {
  const [progress, setProgress] = useState<ScheduleProgress>({
    teachers_imported: false,
    classes_imported: false,
    learners_imported: false,
    total_learners: 0,
    total_classes: 0,
    allocated_slots: 0,
    total_required_slots: 0,
    completion_percentage: 0
  });
  const [loading, setLoading] = useState(true);

  const navigationCards = [
    {
      title: 'Settings',
      description: 'Configure timetable parameters and school settings',
      icon: CogIcon,
      path: '/settings',
      color: 'accent-gold',
    },
    {
      title: 'Inputs',
      description: 'Upload and manage teachers, classes, and line data',
      icon: DocumentTextIcon,
      path: '/inputs',
      color: 'accent-teal',
    },
    {
      title: 'Table Packing',
      description: 'Create and arrange your timetable schedule',
      icon: TableCellsIcon,
      path: '/table-packing',
      color: 'northwood-navy',
    },
    {
      title: 'Outputs',
      description: 'Export and download your completed timetable',
      icon: DocumentArrowDownIcon,
      path: '/outputs',
      color: 'semantic-info',
    },
  ];



  const loadProgress = async () => {
    setLoading(true);
    try {
      // Check if teachers are imported
      const teachersResponse = await teachersApi.getAll();
      const teachersImported = teachersResponse.data.length > 0;

      // Check if classes are imported  
      const classesResponse = await classesApi.getAll();
      const classesImported = classesResponse.data.length > 0;
      const totalClasses = classesResponse.data.length;

      // Check if learners are imported
      const learnersResponse = await learnersApi.getAll({});
      const learnersImported = learnersResponse.data.length > 0;
      const totalLearners = learnersResponse.data.length;

      // Calculate total required slots (sum of allocated_periods for all classes)
      const totalRequiredSlots = classesResponse.data.reduce(
        (sum: number, classItem: any) => sum + (classItem.allocated_periods || 0), 
        0
      );

      // Get current timetable to count allocated slots
      const timetableResponse = await timetableApi.getCurrentTimetable();
      const allocatedSlots = timetableResponse.data.length;

      // Calculate completion percentage
      const completionPercentage = totalRequiredSlots > 0 
        ? (allocatedSlots / totalRequiredSlots) * 100 
        : 0;

      setProgress({
        teachers_imported: teachersImported,
        classes_imported: classesImported,
        learners_imported: learnersImported,
        total_learners: totalLearners,
        total_classes: totalClasses,
        allocated_slots: allocatedSlots,
        total_required_slots: totalRequiredSlots,
        completion_percentage: Math.min(completionPercentage, 100)
      });
    } catch (error) {
      console.error('Error loading progress data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProgress();
  }, []);

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div 
        className="relative bg-gradient-to-r from-northwood-navy to-accent-teal rounded-xl p-12 text-white overflow-hidden"
        style={{
          backgroundImage: "url('/landing_background.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay'
        }}
      >
        <div className="relative z-10">
          <h1 className="text-h1 font-montserrat font-bold mb-4">
            Welcome to Squire Schedule
          </h1>
          <p className="text-xl font-roboto mb-6 max-w-2xl">
            Efficient timetabling solution for Northwood School. 
            Manage your academic schedule with precision and ease.
          </p>
          <Link 
            to="/table-packing" 
            className="btn-secondary inline-block"
          >
            Start Building Schedule
          </Link>
        </div>
        
        {/* Decorative Logo Background */}
        <div className="absolute top-4 right-4 w-32 h-32 bg-white rounded-full flex items-center justify-center p-3">
          <img 
            src="/squire_logo.png" 
            alt="Squire Schedule Logo" 
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      {/* Progress Section */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <ChartBarIcon className="w-8 h-8 text-accent-teal" />
          <h2 className="text-h2 font-montserrat text-northwood-navy">
            Schedule Progress
          </h2>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-teal"></div>
            <span className="ml-3 text-neutral-medium-gray">Loading progress...</span>
          </div>
        ) : (
          <>
            {/* Data Import Checkpoints and Timetable Progress */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Teachers Checkpoint */}
              <div className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                progress.teachers_imported
                  ? 'bg-semantic-success bg-opacity-10 border-semantic-success'
                  : 'bg-northwood-navy bg-opacity-5 border-northwood-navy'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    progress.teachers_imported ? 'bg-semantic-success' : 'bg-northwood-navy'
                  }`}>
                    {progress.teachers_imported ? (
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h4 className={`font-montserrat font-medium ${
                      progress.teachers_imported ? 'text-semantic-success' : 'text-northwood-navy'
                    }`}>
                      Teachers Data
                    </h4>
                    <p className={`text-sm ${
                      progress.teachers_imported ? 'text-semantic-success' : 'text-northwood-navy opacity-70'
                    }`}>
                      {progress.teachers_imported ? 'Imported successfully' : 'Not imported yet'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Classes Checkpoint */}
              <div className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                progress.classes_imported
                  ? 'bg-semantic-success bg-opacity-10 border-semantic-success'
                  : 'bg-northwood-navy bg-opacity-5 border-northwood-navy'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    progress.classes_imported ? 'bg-semantic-success' : 'bg-northwood-navy'
                  }`}>
                    {progress.classes_imported ? (
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h4 className={`font-montserrat font-medium ${
                      progress.classes_imported ? 'text-semantic-success' : 'text-northwood-navy'
                    }`}>
                      Classes Data
                    </h4>
                    <p className={`text-sm ${
                      progress.classes_imported ? 'text-semantic-success' : 'text-northwood-navy opacity-70'
                    }`}>
                      {progress.classes_imported ? `${progress.total_classes} classes imported` : 'Not imported yet'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Learners Checkpoint */}
              <div className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                progress.learners_imported
                  ? 'bg-semantic-success bg-opacity-10 border-semantic-success'
                  : 'bg-northwood-navy bg-opacity-5 border-northwood-navy'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    progress.learners_imported ? 'bg-semantic-success' : 'bg-northwood-navy'
                  }`}>
                    {progress.learners_imported ? (
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h4 className={`font-montserrat font-medium ${
                      progress.learners_imported ? 'text-semantic-success' : 'text-northwood-navy'
                    }`}>
                      Learners Data
                    </h4>
                    <p className={`text-sm ${
                      progress.learners_imported ? 'text-semantic-success' : 'text-northwood-navy opacity-70'
                    }`}>
                      {progress.learners_imported ? `${progress.total_learners} learners imported` : 'Not imported yet'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Timetable Packing Progress */}
              <div className="p-4 rounded-lg border-2 border-northwood-navy bg-northwood-navy bg-opacity-5">
                <div>
                  <h4 className="font-montserrat font-medium text-northwood-navy mb-2">
                    Timetable Progress
                  </h4>
                  {progress.classes_imported ? (
                    <>
                      {/* Progress Bar */}
                      <div className="progress-bar mb-2">
                        <div 
                          className="progress-fill"
                          style={{ width: `${progress.completion_percentage}%` }}
                        />
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-northwood-navy opacity-70">
                          {progress.allocated_slots} / {progress.total_required_slots} periods
                        </span>
                        <span className="text-lg font-montserrat font-bold text-accent-teal">
                          {progress.completion_percentage.toFixed(1)}%
                        </span>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-northwood-navy opacity-70">
                      Import classes data first
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* No Classes Message */}
            {!progress.classes_imported && (
              <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg text-center">
                <div className="flex items-center justify-center space-x-3 mb-2">
                  <DocumentTextIcon className="w-6 h-6 text-blue-500" />
                  <h3 className="font-montserrat font-semibold text-blue-700">
                    Import Your Data First
                  </h3>
                </div>
                <p className="text-blue-600 mb-4">
                  Upload your teachers and classes data to begin building your timetable.
                </p>
                <Link 
                  to="/inputs" 
                  className="btn-primary inline-block"
                >
                  Go to Inputs
                </Link>
              </div>
            )}
          </>
        )}
      </div>

      {/* Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {navigationCards.map((card) => {
          const IconComponent = card.icon;
          return (
            <Link 
              key={card.path}
              to={card.path}
              className="group block"
            >
              <div className="card hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 h-full">
                <div className={`w-12 h-12 bg-${card.color} bg-opacity-10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-opacity-20 transition-colors duration-200`}>
                  <IconComponent className={`w-8 h-8 text-${card.color}`} />
                </div>
                <h3 className="text-xl font-montserrat font-semibold text-northwood-navy mb-2">
                  {card.title}
                </h3>
                <p className="text-neutral-medium-gray font-roboto">
                  {card.description}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default LandingPage;